<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="between">
        <h1 class="title">List of suppliers</h1>
        <app-button v-if="showCreateBtn" theme="export" @click.native="onExport" :class="{'btn--export--loading': export_loading}">
          <img src="@/assets/img/export-icon.svg" alt="Export">
          <span>Export in .xlsx</span>
        </app-button>
      </app-cells>
      <app-cells>
        <app-button v-if="showCreateBtn" @click.native="$modal.show('create-supplier')">Create supplier</app-button>
        <app-input
            @input.native="onListSearch"
            v-model="search"
            class="input--300"
            type="search"
            placeholder="Search by name"
            name="search"
        />
        <app-filters
          v-model="filter"
          :filters="filters"
          name="filterHall"
          @change="onFilterChange"
        />
      </app-cells>
      <v-client-table
          :data="table.items"
          :columns="table.columns"
          :options="table.options"
          class="table-default"
          :class="{ 'table-default--loading': table_preloader }"
      >
        <template #h__name><div class="table-default__left">Name</div></template>
        <template #name="{ row }">
          <div class="table-default__left">
            <router-link :to="{ name: 'supplier-detail', params: { id: row.id } }" class="table-default__link-border">
              {{ row.name }}
            </router-link>
          </div>
        </template>
        <template #orders_ordered="{ row }">{{ row.orders_ordered.toLocaleString() }} TSh</template>
        <template #orders_unpaid="{ row }">{{ row.orders_unpaid.toLocaleString() }} TSh</template>
        <template #orders_invoice_missing="{ row }">{{ row.orders_invoice_missing.toLocaleString() }}</template>
        <template #orders_paid="{ row }">{{ row.orders_paid.toLocaleString() }} TSh</template>
        <template #orders_efd_provided="{ row }">{{ row.orders_efd_provided.toLocaleString() }} TSh</template>
        <template #orders_efd_missing="{ row }">{{ row.orders_efd_missing.toLocaleString() }} TSh</template>
      </v-client-table>
      <app-cells position="between">
        <div>
          <paginate
              :clickHandler="onClickPagination"
              v-if="pagination.pages > 1"
              v-model="pagination.page"
              :page-count="pagination.pages"
              :prev-text="'<'"
              :next-text="'>'"
              :container-class="'pagination'"
              :page-class="'pagination__item'"
              :page-link-class="'pagination__link'"
              :prev-class="'pagination__item pagination__item--prev'"
              :next-class="'pagination__item pagination__item--next'"
              :next-link-class="'pagination__link pagination__link--next'"
              :prev-link-class="'pagination__link pagination__link--prev'"
          />
        </div>
        <app-table-records :all="pagination.count" :count="table.items.length" :page="pagination.page" />
      </app-cells>
    </template>
    <modal
        name="create-supplier"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('create-supplier')" class="popup__close" type="button"></button>
        <div class="popup__title">New supplier</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Name" label-for="supplier_name" required>
              <app-input
                  v-model="form.name"
                  @change.native="$v.form.name.$touch()"
                  :error="$v.form.name.$error"
                  id="supplier_name"
                  placeholder="Name"
                  name="supplier_name"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Payment method" required>
              <app-select
                v-model="form.payment_method"
                :reduce="item => item.code"
                :options="payment_method_options"
                :filterable="false"
                :clearable="false"
                :searchable="false"
                placeholder="Payment method"
                :error="$v.form.payment_method.$error"
              />
              <template #error>
                <div v-if="$v.form.payment_method.$dirty && !$v.form.payment_method.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Create</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getPaymentMethods, getSupplierExport, getSuppliersProduct, postSupplierProduct } from '@/http'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import { format } from 'date-fns'

export default {
  name: 'SuppliersList',
  data() {
    return {
      is_loaded: false,
      export_loading: false,
      search: null,
      table: {
        items: [],
        columns: ['name', 'payment_method.name'],
        options: {
          headings: {
            'name': 'Name',
            'payment_method.name': 'Payment method',
            'orders_ordered': 'Total ordered',
            'orders_unpaid': 'Total Unpaid',
            'invoice_missing': 'Invoice Missing',
            'orders_paid': 'Total Paid',
            'orders_efd_provided': 'EFD Provided',
            'orders_efd_missing': 'EFD Missing',
          },
          pagination: { show: false },
          sortable: [],
          perPage: 20,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      form: {},
      payment_method_options: [],
      filter: '',
      filters: [
        { value: '', label: 'All' },
        { value: 'cash', label: 'Cash' },
        { value: 'bank', label: 'Bank' },
      ],
      table_preloader: false
    }
  },
  validations: {
    form: {
      name: { required },
      payment_method: { required },
    }
  },
  computed: {
    showCreateBtn() {
      return this.$store.state.userGroups.some(group => group.code === 'general_manager' || group.code === 'product_storage_manager')
    }
  },
  created() {
    if (this.showCreateBtn) this.table.columns = ['name', 'payment_method.name', 'orders_ordered', 'orders_unpaid', 'orders_invoice_missing', 'orders_paid', 'orders_efd_provided', 'orders_efd_missing']
    this.fetchItems()
    getPaymentMethods().then(res => { this.payment_method_options = res.data })
  },
  methods: {
    onExport() {
      this.export_loading = true
      getSupplierExport()
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', `Altezza Product storage suppliers ${format(new Date(), 'dd.MM.yyyy HH_mm')}.xlsx`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.export_loading = false
        })
    },
    onFilterChange() {
      this.fetchItems()
    },
    fetchItems() {
      getSuppliersProduct({
        page: this.pagination.page,
        page_size: 20,
        search: this.search,
        payment_method: this.search ? '' : this.filter
      }).then(res => {
        this.table.items = res.data.results
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.is_loaded = true
        this.table_preloader = false
      })
    },
    onCheckForm () {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      postSupplierProduct(this.form)
        .finally(() => { this.$refs.submit.preload = false })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Supplier created',
          })
          this.fetchItems()
          this.$modal.hide('create-supplier')
          this.form = {}
          this.$v.form.$reset()
        })
    },
    onClickPagination(page) {
      this.table_preloader = true
      this.pagination.page = page
      this.fetchItems()
    },
    onListSearch() {
      this.searchList(this)
    },
    searchList: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>
